"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * Form values for login
 */
export interface LoginFormValues {
  email: string
  password: string
}

/**
 * Props for the LoginForm component
 */
export interface LoginFormProps {
  /** Initial form values */
  initialValues?: LoginFormValues
  /** Called when form is submitted with valid values */
  onSubmit: (values: LoginFormValues) => Promise<void>
  /** Whether the form is currently submitting */
  submitting?: boolean
  /** External error message (e.g., from API) */
  error?: string | null
  /** External success message */
  success?: string | null
  /** Called to clear external messages */
  onClearMessages?: () => void
}

/**
 * Validation errors for form fields
 */
interface FieldErrors {
  email?: string
  password?: string
}

// ============================================================================
// Constants
// ============================================================================

const MIN_PASSWORD_LENGTH = 8
const MAX_EMAIL_LENGTH = 255

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validates email address
 */
function validateEmail(email: string): string | undefined {
  const trimmed = email.trim()
  if (!trimmed) {
    return "Email is required"
  }
  if (trimmed.length > MAX_EMAIL_LENGTH) {
    return `Email must be ${MAX_EMAIL_LENGTH} characters or less`
  }
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(trimmed)) {
    return "Please enter a valid email address"
  }
  return undefined
}

/**
 * Validates password
 */
function validatePassword(password: string): string | undefined {
  if (!password) {
    return "Password is required"
  }
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
  }
  return undefined
}

/**
 * Validates all form fields
 */
function validateForm(values: LoginFormValues): FieldErrors {
  return {
    email: validateEmail(values.email),
    password: validatePassword(values.password),
  }
}

/**
 * Checks if there are any validation errors
 */
function hasErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some((error) => error !== undefined)
}

// ============================================================================
// Icons
// ============================================================================

/**
 * Check icon for success state
 */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

/**
 * Alert icon for error state
 */
function AlertIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  )
}

/**
 * Spinner icon for loading state
 */
function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("animate-spin", className)}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

// ============================================================================
// Component
// ============================================================================

/**
 * LoginForm Component
 *
 * A reusable form for user login.
 * Includes validation, loading states, and success/error feedback.
 *
 * @example
 * ```tsx
 * <LoginForm
 *   initialValues={{ email: "", password: "" }}
 *   onSubmit={async (values) => { await login(values) }}
 *   submitting={isSubmitting}
 *   error={errorMessage}
 *   success={successMessage}
 * />
 * ```
 */
export function LoginForm({
  initialValues = { email: "", password: "" },
  onSubmit,
  submitting = false,
  error = null,
  success = null,
  onClearMessages,
}: LoginFormProps) {
  // Form state
  const [values, setValues] = React.useState<LoginFormValues>(initialValues)
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})

  // Update form values when initial values change (e.g., after fetch)
  React.useEffect(() => {
    setValues(initialValues)
  }, [initialValues])

  /**
   * Handle input change
   */
  const handleChange = (field: keyof LoginFormValues) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value

    setValues((prev) => ({
      ...prev,
      [field]: newValue,
    }))

    // Clear field error on change
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }))
    }

    // Clear external messages on change
    if (onClearMessages && (error || success)) {
      onClearMessages()
    }
  }

  /**
   * Handle input blur for validation
   */
  const handleBlur = (field: keyof LoginFormValues) => () => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }))

    // Validate the field
    const fieldError =
      field === "email"
        ? validateEmail(values.email)
        : validatePassword(values.password)

    setErrors((prev) => ({
      ...prev,
      [field]: fieldError,
    }))
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Clear any external messages
    if (onClearMessages) {
      onClearMessages()
    }

    // Validate all fields
    const validationErrors = validateForm(values)
    setErrors(validationErrors)
    setTouched({
      email: true,
      password: true,
    })

    // If there are errors, don't submit
    if (hasErrors(validationErrors)) {
      return
    }

    // Submit the form
    await onSubmit(values)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Success Message */}
      {success && (
        <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 p-4 text-green-800">
          <CheckIcon className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{success}</p>
        </div>
      )}

      {/* Error Message (external) */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 border border-destructive/20 p-4 text-destructive">
          <AlertIcon className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Email Field */}
      <div className="space-y-2">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-foreground"
        >
          Email
        </label>
        <Input
          id="email"
          type="email"
          value={values.email}
          onChange={handleChange("email")}
          onBlur={handleBlur("email")}
          placeholder="you@example.com"
          disabled={submitting}
          className={cn(
            touched.email && errors.email
              ? "border-destructive focus-visible:ring-destructive"
              : ""
          )}
          aria-invalid={touched.email && !!errors.email}
          aria-describedby={
            touched.email && errors.email
              ? "email-error"
              : undefined
          }
        />
        {touched.email && errors.email && (
          <p
            id="email-error"
            className="text-sm text-destructive flex items-center gap-1"
          >
            <AlertIcon className="h-4 w-4" />
            {errors.email}
          </p>
        )}
      </div>

      {/* Password Field */}
      <div className="space-y-2">
        <label
          htmlFor="password"
          className="block text-sm font-medium text-foreground"
        >
          Password
        </label>
        <Input
          id="password"
          type="password"
          value={values.password}
          onChange={handleChange("password")}
          onBlur={handleBlur("password")}
          placeholder="Enter your password"
          disabled={submitting}
          className={cn(
            touched.password && errors.password
              ? "border-destructive focus-visible:ring-destructive"
              : ""
          )}
          aria-invalid={touched.password && !!errors.password}
          aria-describedby={
            touched.password && errors.password
              ? "password-error"
              : undefined
          }
        />
        {touched.password && errors.password && (
          <p
            id="password-error"
            className="text-sm text-destructive flex items-center gap-1"
          >
            <AlertIcon className="h-4 w-4" />
            {errors.password}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex items-center gap-4 pt-4">
        <Button type="submit" disabled={submitting} className="w-full">
          {submitting ? (
            <>
              <SpinnerIcon className="mr-2 h-4 w-4" />
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </Button>
      </div>
    </form>
  )
}

export default LoginForm
